import { generatePath } from 'react-router';
import { RxSocketUrls } from '@Constants/CommonConstants';
import { config as CONSTANTS } from '@Constants/rx-ui/constants';
import { EXPORT_EVENTS } from '@Services/ExportService/ExportConstants';
import { PROFILE_ERRORS_LIST } from '@Pages/ProfileSettings/ProfileSettingsConstants';
import Search from '@Services/Search';
import NavigationHistory from '@Services/NavigationHistory';
import some from 'lodash/some';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import isUndefined from 'lodash/isUndefined';
import extend from 'lodash/extend';
import isEmpty from 'lodash/isEmpty';
import capitalize from 'lodash/capitalize';
import { loginLimitErrorType } from '@Pages/LoginLimit/LoginLimitConstants';
import { dispatchOpenUnavailableResultsDialog, validateSocketError as isResultNameSocketError } from '@Containers/ResultSetValidation';
import { unavailableResultsMessage } from '@Containers/ResultSetValidation/ResultSetValidationConstants';
import AppStorage from '@Services/AppStorage';
import Catalyst from '@Services/Catalyst';
import RXService from '@Services/RxService';
import { generateTraceHeaders } from '@Services/CorrelationId';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { typeOfUnavailableResultsDialog } from '@Containers/ResultSetValidation/UnavailableResultsDialog';
import SocketBroker from '@Services/SocketBroker';
import RxSocket from '@Services/RxSocket';
import Auth from '@Services/Auth';
import RequestUrls from '@Constants/RequestUrlsConstants';
import { paths } from '@AppRoutes/constants';
import { getTrackingPathName } from '@AppRoutes/AppRoutesSelectors';
import history from '@AppRoutes/history';
import store from '@Store';

const HTML_CONTENT_TYPES = ['text/html', 'application/xhtml+xml', 'multipart/form-data'];
const idleModalUrl = 'common/rx-user/views/idle-warning.html';
const rxModalUrl = 'common/rx-ui/views/rx-modal/rx-modal.html';

// eslint-disable-next-line import/no-mutable-exports
export let errorModalActive = false;

// eslint-disable-next-line no-shadow
function _handleActiveLoginsLimit(response) {
  history.push({
    pathname: paths.LOGIN_LIMIT,
    state: {
      message: get(response, 'data.message'),
    },
  });
}

function _handleUnavailableResults() {
  dispatchOpenUnavailableResultsDialog();
  Auth.goToLandingPage(true);
}

// eslint-disable-next-line no-shadow
function _isNoReload(stateCurrentName) {
  return stateCurrentName.indexOf('public.register') >= 0 || stateCurrentName.indexOf('public.login') >= 0;
}

// eslint-disable-next-line no-shadow
export function _mainToPublic(response, content) {
  const userData = AppStorage.instance.get('userInstitutionInfo');
  const isRememberChoice = userData && userData.rememberChoice;
  // eslint-disable-next-line no-param-reassign
  content.isClean = true;
  const stateCurrentName = getTrackingPathName(store.getState()) || '';
  if (Auth.isLoggedIn()) {
    const configData = get(response, 'config.data', '');
    if (isString(configData) && configData.indexOf('accessToken') >= 0) {
      return; // session upgrade with failed password
    }
    if (stateCurrentName.indexOf('public.') < 0 && stateCurrentName.indexOf('register') < 0) {
      Auth.cleanUp();
    } else if (stateCurrentName.indexOf('public.redirect') >= 0 && response.status === 401) {
      Auth.setRememberMeToken();
    }
    window.location.replace('/');
  } else if (isRememberChoice) {
    history.push({
      pathname: paths.LOGIN_INSTITUTION,
    });
    // eslint-disable-next-line no-empty
  } else if (_isNoReload(stateCurrentName)) {
  } else {
    // revisit last state if it is whitelisted
    const lastVisitedState = NavigationHistory.getLastVisitedStateForLogin();

    if (lastVisitedState) {
      AppStorage.instance.set('revisitLastState', true);
    }

    history.push({
      pathname: generatePath(paths.LOGIN),
      state: {
        loginFail: Auth.isGrantAnonymoususer() ? null : content?.text,
      },
    });
  }
}

// eslint-disable-next-line no-shadow
export function _showGenericErrorModal(response, isGoToLogin, modalProps = {}) {
  const { level, title, msg, link } = modalProps;
  const url = get(response, 'config.url', '');
  const type = get(response, 'data.type');
  const { status } = response;

  const XMLorAEIsDown = url.indexOf('/oauth/token') >= 0 && (type === 'EXTERNAL' || type === 'ERROR_RETRY');
  const BEIsDown = status === 404 || status === 504;
  const isErrorHealthcheck = url.indexOf('healthcheck?forwardedChecks=true&xmlAndAeChecksOnly=true') > 0;
  if (isGoToLogin) {
    // eslint-disable-next-line no-use-before-define
    if (getErrorLevel(response) === 'fatal') {
      Auth.logoutOnExpiredSession(null, msg, false, true);
    } else if (XMLorAEIsDown || BEIsDown || isErrorHealthcheck) {
      return;
    } else {
      _mainToPublic(response, { text: msg });
    }
    return;
  }

  if (errorModalActive) {
    return;
  }

  errorModalActive = true;

  NotifyDialogsService.openModalWindowPromise({
    type: level,
    confirmText: 'Continue',
    traceparent: get(response, 'data.traceparent', '') || get(response, 'config.headers.traceparent', ''),
    timestamp: get(response, 'data.timestamp', new Date().toString()),
    title,
    text: msg,
    link,
  })
    .then(() => {
      errorModalActive = false;
    })
    .catch(Logger.error);
}

// eslint-disable-next-line no-shadow
export function trackResponseError(response, error) {
  Catalyst.trackError(
    {
      ...response,
      ...error,
      statusCode: response.status,
      errorNumber: response.errorNumber || get(response, 'data.errorNumber') || error.errorNumber,
      component: response.component || get(response, 'data.component') || error.component,
    },
    'se-'
  );
}

// eslint-disable-next-line no-shadow
function getErrorLevel(response) {
  return get(response, 'data.level', 'error')?.toLowerCase();
}

// eslint-disable-next-line no-shadow
function _handleDataChoice(response) {
  history.push({
    pathname: paths.LOGIN_ORGANIZATION,
    state: {
      response,
    },
  });
}

// eslint-disable-next-line no-shadow
export function getGenericCDNErrorMessage(response, defaultTitle) {
  let title = defaultTitle;
  let msg =
    'Something goes wrong\n' +
    '\n' +
    'Please retry your last action in a few minutes. If it fails again, contact the Reaxys Customer Care team.';

  let link = {
    url: 'https://service.elsevier.com/app/contact/supporthub/reaxys/',
    linkText: 'click there',
    // eslint-disable-next-line no-useless-concat
    preMessage: '\n' + 'If the problem persists,',
    postMessage: 'to contact the Reaxys Customer Care Team.\n',
  };

  const definedErrorMsg =
    'Something has gone wrong on our side. We are working on fixing this.\n' +
    '\n' +
    'In a few minutes please try your last action again. You could try refreshing the page or clearing your cache, which sometimes helps.\n';

  if (response.status) {
    // eslint-disable-next-line default-case
    switch (response.status) {
      case 400:
        title = '400 Bad Request Error';
        msg = 'The server was not able to process your last action, please try again.\n';
        break;
      case 429:
        title = '429 Too Many Requests';
        msg = 'You have reached the limit of concurrent requests. Please wait 2 minutes and try your last action again.\n';
        link = {
          url: 'https://service.elsevier.com/app/contact/supporthub/reaxys/',
          linkText: 'click there',
          // eslint-disable-next-line no-useless-concat
          preMessage: '\n' + 'If you believe that the limit is incorrectly applied, then please',
          postMessage: 'to contact the Reaxys Customer Care Team.\n',
        };
        break;
      case 500:
        title = '500 Internal Server Error';
        msg =
          'Something has gone wrong on our side. We are working on fixing this.\n' +
          '\n' +
          'In a few minutes please try your last action again. You could try refreshing the page or clearing your cache, which sometimes helps.\n';
        break;
      case 502:
        title = '502 Bad Gateway Error';
        msg = definedErrorMsg;
        break;
      case 503:
        title = '503 Service Unavailable Error';
        msg = definedErrorMsg;
        break;
      case 504:
        title = '504 Timeout';
        msg = definedErrorMsg;
        break;
    }
  }
  return [title, msg, link];
}

// eslint-disable-next-line no-shadow
export function _openGenericErrorModal(response, isGoToLogin) {
  const level = getErrorLevel(response);
  let title = get(response, 'data.title', capitalize(level));
  let msg = '';
  let link;

  if (isString(response.data)) {
    if (
      response.headers &&
      response.headers('Content-Type') &&
      some(HTML_CONTENT_TYPES, (type) => includes(response.headers('Content-Type').toLowerCase(), type))
    ) {
      // if error response is string and content type HTML replace message with readable text
      [title, msg, link] = getGenericCDNErrorMessage(response, title);
    } else {
      msg = response.data;
    }
  } else if (isObject(response.data)) {
    const sm = get(response, 'data.message', '').replace(/;\d.*$/, '.');
    const lm = response.data.longMessage;
    const arrays = [sm !== lm && sm, sm.indexOf(lm) === -1 && lm, response.data.validationErrors];
    const flattenedArray = arrays.reduce((acc, val) => acc.concat(val), []);
    const compactArray = flattenedArray.filter(Boolean);
    msg = compactArray.join('\n ');
  } else {
    msg = 'Generic error';
  }

  trackResponseError(response, {
    message: msg,
  });

  return _showGenericErrorModal(response, isGoToLogin, {
    level,
    title,
    link,
    msg,
  });
}

// eslint-disable-next-line no-shadow
export function responseError(response, info) {
  if (get(response, 'xhrStatus') === 'abort') {
    return Promise.reject(response);
  }

  const reqUrl = get(response, 'config.url', '');

  const locationUrlWithHash = window.location.hash;
  const locationUrl = locationUrlWithHash.replace(/^#/, '');

  if (isUndefined(response) || isUndefined(response.data) || isUndefined(response.status)) {
    return Promise.reject(response);
  }

  if (response.data && response.data.hasUIHandler) {
    return Promise.reject(response);
  }

  if (locationUrl.indexOf('/hopinto') >= 0) {
    // store hopinto only when error is related to unauthorized session
    if (response.status === 401) {
      AppStorage.instance.set('hopintoPath', locationUrl);
    }
  }

  // In case when 404 error code is valid and means "resource not found"
  if (get(response, 'config.validErrorStatusCodes', []).indexOf(response.status) !== -1) {
    return Promise.reject(response);
  }

  // custom setting to not intercept errors
  if (get(response, 'config.ignoreIntercepts')) {
    return Promise.reject(response);
  }

  const queryId = get(response, 'data.queryId');
  if (queryId) {
    SocketBroker.stop(queryId);
  }

  if (PROFILE_ERRORS_LIST.includes(get(response, 'data.errorNumber'))) {
    // custom handler in ProfileSettings factory
    return Promise.reject(response);
  }
  if (response.status === 401 && response.data.type === 'CHOICE') {
    // only after login with departments
    _handleDataChoice(response);
    return Promise.reject(response);
  }
  if (response.status === 401 && response.data.type === loginLimitErrorType) {
    _handleActiveLoginsLimit(response);
    trackResponseError(response, {
      message: get(response, 'data.message') || 'Too many current active logins.',
      errorNumber: loginLimitErrorType,
    });
    return Promise.reject(response);
  }
  if (response.status === 401 || response.status === 403) {
    // auth/session errors
    if (RequestUrls.userLogout.urlRegex.test(reqUrl)) {
      return Promise.reject(response); // if it's disconnect but session is already dead on BE, we ignore
    }
    if (info && RxSocket.getConnectUrl() && Auth.getAccessToken() && RxSocket.getConnectUrl().indexOf(Auth.getAccessToken()) === -1) {
      trackResponseError(response, {
        message: 'Session loss when got 401 via WS because of access tokens mismatch',
        errorNumber: 'tokenMismatch',
        component: 'WebSocket',
      });
      // If 401 via WS and tokens don't match - do nothing
      extend(response.data, {
        longMessage: 'Session loss when got 401 via WS because of access tokens mismatch',
      });
    } else {
      _openGenericErrorModal(response, true);
    }
  } else if (response.status === 400 && RequestUrls.changePassword.urlRegex.test(reqUrl)) {
    // change pass
    return Promise.reject(response);
  } else if (response.status === 404 || response.status === -1) {
    // loading result images, when server is slower client and connection issues and missing CORS

    if (RequestUrls.userToken.url === reqUrl) {
      _openGenericErrorModal({ data: "It's Maintenance time. We'll be back soon, try again later." }, true);
    }
  } else if (
    response.status === 400 &&
    (response.data.errorNumber === 15 || // export failed by xml server
      response.data.errorNumber === 24) // restore failed because of job expired
  ) {
    // export
    RXService.next(EXPORT_EVENTS.error, response, info); // RXFE-2093: we have custom handler for export into export.controller
  } else if (response.status === 400 && response.data.errorNumber === 8) {
    // ^ https://jira.elsevier.com/browse/RX-3979 if we got error from register form - do not show additional error modal
    return Promise.reject(response);
  } else if (
    response.status === 400 &&
    reqUrl.indexOf(RequestUrls.checkToken.url) !== -1 &&
    get(response, 'data.error') === 'invalid_token'
  ) {
    // ^ RXFE-6345 if we got error from check_token request - redirect to landing
    _openGenericErrorModal(response, true);
  } else if (isResultNameSocketError(response)) {
    trackResponseError(response, {
      message: get(response, 'message') || get(response, 'data.message') || unavailableResultsMessage,
      errorNumber: typeOfUnavailableResultsDialog,
    });
    // RX-958 - socket error related to invalid result name
    _handleUnavailableResults();
  } else if (response.status === 429 && get(response, 'data.errorNumber') === 27) {
    // RX-2018 - socket error related to search and retrieval Rate Limit hit
    const level = getErrorLevel(response);
    const [title, msg, link] = getGenericCDNErrorMessage(response, get(response, 'data.title', capitalize(level)));
    _showGenericErrorModal(response, false, {
      level,
      title,
      msg,
      link,
    });

    trackResponseError(response, {
      message: msg,
      component: 'WebSocket',
    });
  } else if (response.status === 500 && response.data.errorNumber === 12) {
    const responseRequestId = get(info, 'requestId');
    if (Search.isSearchCancelled(responseRequestId)) {
      Search.removeCancelledSearch(responseRequestId);
      // eslint-disable-next-line consistent-return
      return;
    }
    _openGenericErrorModal(response);
  } else if (!response.config || !response.config.ignoreErrorModal) {
    // any other error
    _openGenericErrorModal(response);
  } else if (response.status === 400 && response.data.errorNumber === 5) {
    /**
     * RXFE-5002: HTTP response error has errorNumber 5 and status code 400, if we do not show error modal after
     * HTTP error response we will try to do it after callback from WS
     */
    _openGenericErrorModal(response);
  }
  return Promise.reject(response);
}

function isWebsocketRequest(config) {
  const url = get(config, 'url', '');
  if (isString(url)) {
    return url.indexOf(RxSocketUrls.ConnectionUrl) === -1;
  }
  return false;
}

function isIdleModalRequest(config) {
  const url = get(config, 'url', '');
  if (isString(url)) {
    return url.indexOf(idleModalUrl) !== -1;
  }
  return false;
}

function isModalRequest(config) {
  const url = get(config, 'url', '');
  if (isString(url)) {
    return url.indexOf(rxModalUrl) !== -1;
  }
  return false;
}

export function request(config) {
  if (isWebsocketRequest(config) && !isIdleModalRequest(config) && !isModalRequest(config)) {
    Auth.setLastActionTime(); // do not consider websocket reconnection as a user activity
  }
  const token = Auth.getAccessToken();

  // request to public service should not contain authorization header
  if (
    token &&
    config.Authorization !== false &&
    !config.ignoreAllSpecificHeaders &&
    config.url &&
    config.url.indexOf('/public/') < 0 &&
    config.url.indexOf('.html') < 0
  ) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Feature switching beta, only boolean true are taken
  const devOptions = Auth.getDevOptions();
  if (!isEmpty(devOptions)) {
    // eslint-disable-next-line no-param-reassign
    config.headers[CONSTANTS.CONTROL_HEADER] = devOptions;
  }
  extend(config.headers, generateTraceHeaders());

  return config;
}

export function response(data) {
  // probably getValueByNs(data, 'config.headers.Authorization', false)
  if (data && /^(f|ht)tps?:\/\//i.test(data.config.url) && isWebsocketRequest(data.config)) {
    Auth.setLastActionTime(); // do not consider websocket reconnection as a user activity
  }

  return data || Promise.resolve(data);
}
